import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Typography,
  message,
} from 'antd'
import { adminApi } from '../../api'
import { PageHeader } from '../../components/ui'

type StorageProvider = { id: string; label: string }

type StorageConfig = {
  provider: string
  path_prefix: string
  sign_url_ttl_seconds: number
  bucket: string
  region: string
  endpoint: string
  path_style: boolean
  access_key_id: string
  access_key_secret?: string
  access_key_secret_set?: boolean
  local_upload_root?: string
  providers?: StorageProvider[]
}

const CLOUD_PROVIDERS = new Set(['aliyun_oss', 'tencent_cos', 'huawei_obs', 's3'])

export default function StorageSettings() {
  const [form] = Form.useForm<StorageConfig>()
  const [messageApi, messageContext] = message.useMessage()
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [providers, setProviders] = useState<StorageProvider[]>([])
  const [localRoot, setLocalRoot] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [secretSet, setSecretSet] = useState(false)
  const loadVersion = useRef(0)
  const provider = Form.useWatch('provider', form) || 'local'
  const isCloud = CLOUD_PROVIDERS.has(provider)

  const loadConfig = useCallback(() => {
    const version = ++loadVersion.current
    return adminApi.get<StorageConfig>('/admin/settings/storage').then((r) => {
      if (version !== loadVersion.current) return
      setProviders(r.data.providers || [])
      setLocalRoot(r.data.local_upload_root || '')
      setSecretSet(!!r.data.access_key_secret_set)
      form.setFieldsValue({
        provider: r.data.provider || 'local',
        path_prefix: r.data.path_prefix || '',
        sign_url_ttl_seconds: r.data.sign_url_ttl_seconds ?? 3600,
        bucket: r.data.bucket || '',
        region: r.data.region || (r.data.provider === 's3' ? 'us-east-1' : ''),
        endpoint: r.data.endpoint || '',
        path_style: r.data.path_style ?? true,
        access_key_id: r.data.access_key_id || '',
        access_key_secret: '',
      })
      setDirty(false)
      setLoaded(true)
    }).catch((error) => {
      if (version === loadVersion.current) throw error
    })
  }, [form])

  useEffect(() => {
    loadConfig().catch((e) => messageApi.error(e.message))
    return () => {
      // StrictMode 重放和页面卸载后，旧响应及错误都不能再影响表单。
      loadVersion.current += 1
    }
  }, [loadConfig, messageApi])

  const save = () => {
    void form.validateFields().then((v) => {
      setLoading(true)
      const payload = { ...v }
      if (payload.provider === 's3') {
        payload.region = payload.region?.trim() || 'us-east-1'
        payload.path_style = payload.path_style ?? true
      }
      if (!payload.access_key_secret) {
        delete payload.access_key_secret
      }
      adminApi
        .put('/admin/settings/storage', payload)
        .then(() => {
          messageApi.success('存储配置已保存')
          return loadConfig()
        })
        .catch((e) => messageApi.error(e.message))
        .finally(() => setLoading(false))
    }).catch(() => { /* 表单校验错误由 Form.Item 展示。 */ })
  }

  const testConn = () => {
    if (!loaded || dirty || loading) return
    setTesting(true)
    adminApi
      .post<{ ok: boolean; message: string }>('/admin/settings/storage/test')
      .then((r) => {
        if (r.data.ok) messageApi.success(r.data.message || '已保存配置测试通过')
        else messageApi.error(r.data.message || '已保存配置测试失败')
      })
      .catch((e) => messageApi.error(e.message))
      .finally(() => setTesting(false))
  }

  const providerOptions =
    providers.length > 0
      ? providers.map((p) => ({ value: p.id, label: p.label }))
      : [
          { value: 'local', label: '本地磁盘' },
          { value: 'aliyun_oss', label: '阿里云 OSS' },
          { value: 'tencent_cos', label: '腾讯云 COS' },
          { value: 'huawei_obs', label: '华为云 OBS' },
          { value: 's3', label: 'S3 兼容存储（MinIO）' },
        ]

  return (
    <div>
      {messageContext}
      <PageHeader
        title="存储管理"
        description="配置知识库附件的物理存储位置，支持本地磁盘、云存储及 S3 兼容存储（MinIO）。对象存储下载经后端签名 URL 或同源代理。"
      />

      <Card>
        <Form
          form={form}
          layout="vertical"
          style={{ maxWidth: 720 }}
          disabled={!loaded || loading || testing}
          onValuesChange={() => {
            loadVersion.current += 1
            setDirty(true)
          }}
        >
          <Form.Item name="provider" label="存储类型" rules={[{ required: true }]}>
            <Select options={providerOptions} />
          </Form.Item>

          <Form.Item
            name="path_prefix"
            label="对象路径前缀"
            extra="附件保存在此前缀下，系统自动附加存储位置标识、日期和文件名。"
          >
            <Input placeholder="留空则无前缀" />
          </Form.Item>

          {isCloud ? (
            <>
              <Form.Item
                name="bucket"
                label="Bucket 名称"
                rules={[{ required: true }]}
                extra="仅填桶名，例如 ai-archive-build，不要带 https://"
              >
                <Input placeholder="例如 ai-archive-build" />
              </Form.Item>

              <Form.Item
                name="region"
                label="Region"
                rules={provider === 'tencent_cos' ? [{ required: true }] : []}
                extra={
                  provider === 'tencent_cos'
                    ? '例如 ap-guangzhou'
                    : provider === 's3'
                      ? '按存储服务填写；留空使用 us-east-1'
                      : '腾讯云必填；阿里云/华为云可填区域标识便于识别'
                }
              >
                <Input placeholder={provider === 's3' ? 'us-east-1' : '区域'} />
              </Form.Item>

              <Form.Item
                name="endpoint"
                label="Endpoint"
                rules={
                  provider === 'tencent_cos'
                    ? []
                    : [{ required: true, message: '请填写 Endpoint' }]
                }
                extra={
                  provider === 'aliyun_oss'
                    ? '只填地域节点，不要带 Bucket 名。正确示例：https://oss-cn-beijing.aliyuncs.com（注意是 https 不是 htttps）'
                    : provider === 'huawei_obs'
                      ? '例如 https://obs.cn-north-4.myhuaweicloud.com'
                      : provider === 's3'
                        ? '填写 S3 API 地址，保留实际协议和端口，例如 http://minio.weaverdoc.com:5051；不要填写管理控制台地址或 Bucket 路径'
                        : '可选；留空则按 Region 使用默认域名（如 cos.ap-shanghai.myqcloud.com）'
                }
              >
                <Input
                  placeholder={
                    provider === 'tencent_cos'
                      ? '留空即可，或 cos.ap-shanghai.myqcloud.com'
                      : provider === 'huawei_obs'
                        ? 'https://obs.cn-north-4.myhuaweicloud.com'
                        : provider === 's3'
                          ? 'http://minio.weaverdoc.com:5051'
                          : 'https://oss-cn-beijing.aliyuncs.com'
                  }
                />
              </Form.Item>

              {provider === 's3' && (
                <Form.Item
                  name="path_style"
                  label="Bucket 寻址方式"
                  extra="MinIO 通常使用路径式；仅在服务已配置 Bucket 子域名时选择虚拟主机式。"
                >
                  <Radio.Group options={[
                    { value: true, label: '路径式（Path-style）' },
                    { value: false, label: '虚拟主机式（Virtual-hosted）' },
                  ]} />
                </Form.Item>
              )}

              <Form.Item name="access_key_id" label="AccessKey ID / SecretId" rules={[{ required: true }]}>
                <Input placeholder="AK / SecretId" autoComplete="off" />
              </Form.Item>

              <Form.Item
                name="access_key_secret"
                label="AccessKey Secret / SecretKey"
                rules={[{ required: !secretSet, message: '请填写 SecretKey' }]}
                extra={secretSet ? '密钥已保存，不回显。留空保存保留原密钥；填写新值则替换。' : '首次配置请填写密钥，保存后不回显。'}
              >
                <Input.Password placeholder={secretSet ? '已保存，留空保持不变' : 'SK / SecretKey'} autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                name="sign_url_ttl_seconds"
                label="下载签名有效期（秒）"
                rules={[{ required: true }]}
                extra="用户下载或预览时，后端生成该有效期的签名 URL 并 302 重定向"
              >
                <InputNumber min={60} max={86400} style={{ width: '100%' }} />
              </Form.Item>
            </>
          ) : (
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                title="本地磁盘模式"
                description={
                  <>
                    <div>
                      新上传的附件将保存在后端服务器本地目录：
                      <Typography.Text code copyable={!!localRoot}>
                        {localRoot || '（保存后显示实际路径）'}
                      </Typography.Text>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      Docker 部署时该目录对应卷 <Typography.Text code>app_uploads</Typography.Text>（容器内一般为{' '}
                      <Typography.Text code>/app/data/uploads</Typography.Text>）。请确保磁盘空间充足并做好备份。
                    </div>
                    <div style={{ marginTop: 8 }}>
                      切换存储只影响新上传附件，历史附件继续从原位置读取。请保留原存储服务、文件及有效凭据。
                    </div>
                  </>
                }
              />
            </>
          )}

          <Space>
            <Button type="primary" loading={loading} onClick={save}>
              保存配置
            </Button>
            <Button loading={testing} disabled={!loaded || dirty || loading} onClick={testConn}>
              测试已保存配置
            </Button>
          </Space>
          <div style={{ marginTop: 8 }}>
            <Typography.Text type={dirty ? 'warning' : 'secondary'}>
              {dirty ? '有未保存更改，请先保存配置再测试。' : '连接测试使用已保存配置，不会测试尚未保存的表单内容。'}
            </Typography.Text>
          </div>
        </Form>
      </Card>
    </div>
  )
}
